import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getSupabaseClient } from "../providers/supabase/supabase";

// Acquisition deal board (Stage 2a, D27) — a view grouped by the admin-configurable
// deal_stages. No folder objects. Track-only: cards link to the deal record.

type Stage = { id: string; key: string; name: string; position: number; is_terminal: boolean };
type Deal = {
  id: string; deal_stage_id: string; contact_name: string | null; address_line1: string | null;
  city: string | null; offer_price: number | null; expected_profit: number | null; assignee_name: string | null;
};

const money = (n: number | null) => (n == null ? null : `$${Number(n).toLocaleString()}`);

const DealCard = ({ d }: { d: Deal }) => (
  <Link to={`/deals/${d.id}`} className="block">
    <Card className="mb-2 hover:border-amber-400 transition-colors">
      <CardContent className="p-3">
        <div className="font-semibold text-sm truncate">{d.contact_name || "(no name)"}</div>
        <div className="text-xs text-muted-foreground truncate">{[d.address_line1, d.city].filter(Boolean).join(", ") || "—"}</div>
        <div className="mt-1 flex items-center gap-2 text-xs">
          {d.offer_price != null && <Badge variant="outline">offer {money(d.offer_price)}</Badge>}
          {d.expected_profit != null && <span className="text-green-700 dark:text-green-400">▲ {money(d.expected_profit)}</span>}
          {d.assignee_name && <span className="ml-auto text-muted-foreground">{d.assignee_name}</span>}
        </div>
      </CardContent>
    </Card>
  </Link>
);

const DealsPage = () => {
  const [stages, setStages] = useState<Stage[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const sb = getSupabaseClient();
      const [{ data: st }, { data: dl }] = await Promise.all([
        sb.from("deal_stages").select("*").order("position"),
        sb.from("deals_summary").select("id,deal_stage_id,contact_name,address_line1,city,offer_price,expected_profit,assignee_name").eq("active", true),
      ]);
      setStages((st as Stage[]) ?? []);
      setDeals((dl as Deal[]) ?? []);
      setLoading(false);
    })();
  }, []);

  const columns = stages.filter((s) => !s.is_terminal);
  const byStage: Record<string, Deal[]> = {};
  columns.forEach((s) => (byStage[s.id] = []));
  deals.forEach((d) => byStage[d.deal_stage_id]?.push(d));

  return (
    <div className="p-4">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Acquisition Pipeline</h1>
          <div className="text-sm text-muted-foreground">Deals handed off from the Marketing Engine. Track-only — numbers are entered.</div>
        </div>
      </div>
      {loading ? <p className="text-sm text-muted-foreground p-2">Loading…</p>
        : deals.length === 0
          ? <p className="text-sm text-muted-foreground p-2">No active deals. Create one from a qualified lead's page.</p>
          : (
            <div className="flex gap-3 overflow-x-auto pb-4">
              {columns.map((s) => (
                <div key={s.id} className="min-w-[250px] w-[250px] flex-shrink-0">
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="text-sm font-semibold">{s.name}</h3>
                    <span className="text-xs text-muted-foreground">{byStage[s.id].length}</span>
                  </div>
                  <div className="min-h-[60px]">{byStage[s.id].map((d) => <DealCard key={d.id} d={d} />)}</div>
                </div>
              ))}
            </div>
          )}
    </div>
  );
};

DealsPage.path = "/deals";
export default DealsPage;
